import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import ButtonConfirmDialog from "./ButtonConfirmDialog";
import { Box, Grid } from "@material-ui/core";
import { Save as SaveIcon } from "@material-ui/icons";
import { Delete as DeleteIcon } from "@material-ui/icons";

interface Props {
  steps: string[];
  children?: any;
  previousButtonText?: string;
  nextButtonText?: string;
  lastStepButtonText?: string;
  confirmationModalTitle?: string;
  confirmationModalMessage?: string;
  confirmationModalOkBtnText?: string;
  confirmationModalCancelBtnText?: string;
  onFinish?: () => void;
  saveBtn?: boolean;
  saveBtnText?: string;
  saveModalTitle?: string;
  saveModalMessage?: string;
  saveModalOkBtnText?: string;
  saveModalCancelBtnText?: string;
  onSave?: () => Promise<void>;
  deleteBtn?: boolean;
  deleteBtnText?: string;
  deleteModalTitle?: string;
  deleteModalMessage?: string;
  deleteModalOkBtnText?: string;
  deleteModalCancelBtnText?: string;
  onDelete?: () => void;
  isFinancingRequest: boolean;
}

export default function StepperComponent({
  steps,
  children,
  previousButtonText,
  nextButtonText,
  lastStepButtonText,
  confirmationModalTitle,
  confirmationModalMessage,
  confirmationModalOkBtnText,
  confirmationModalCancelBtnText,
  onFinish,
  saveBtn,
  saveBtnText,
  saveModalTitle,
  saveModalMessage,
  saveModalOkBtnText,
  saveModalCancelBtnText,
  onSave,
  deleteBtn,
  deleteBtnText,
  deleteModalTitle,
  deleteModalMessage,
  deleteModalOkBtnText,
  deleteModalCancelBtnText,
  onDelete,
  isFinancingRequest
}: Props) {
  const classes = useStyles({});
  const [activeStep, setActiveStep] = useState<number>(0);

  let handlePreviousClick = () => { };
  let handleNextClick = () => { };
  let handleSaveClick = (): any => { };
  let handleDeleteClick = () => { };

  const handleSave = async () => {
    if (onSave) {
      const saveClick = handleSaveClick();
      if (saveClick == null || (saveClick != null && saveClick != false)) {
        await onSave();
      }
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      handleDeleteClick();
      onDelete();
    }
  };

  const handlePrevious = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1);
  };

  const isFirstStep = (): boolean => {
    return activeStep === 0;
  };

  const isLastStep = (): boolean => {
    return steps.length === activeStep + 1;
  };

  const isFourthStep = (): boolean => {
    return activeStep === 3;
  };


  const getStepContent = (stepIndex: number) => {
    const child = children[stepIndex];
    if (typeof child.type === "string") return child;
    return React.cloneElement(child, {
      goToPreviousStep: handlePrevious,
      goToNextStep: handleNext,
      onPrevious: (click: any) => {
        handlePreviousClick = click;
      },
      onNext: (click: any) => {
        handleNextClick = click;
      },
      onStepperSave: (click: any) => {
        handleSaveClick = click;
      },
      buttons: (
        <Box mt={"20px"}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              {!isFirstStep() ? (
                <Box textAlign="left">
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => handlePreviousClick()}
                  >
                    {previousButtonText}
                  </Button>
                </Box>
              ) : null}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box textAlign="right">
                {isLastStep() ? (
                  <ButtonConfirmDialog
                    btnText={lastStepButtonText || nextButtonText || ""}
                    title={confirmationModalTitle || ""}
                    message={confirmationModalMessage || ""}
                    cancelBtnText={confirmationModalCancelBtnText || ""}
                    okBtnText={confirmationModalOkBtnText || ""}
                    onOkClick={onFinish}
                  />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      if (isFinancingRequest === true && isFourthStep() && onSave) {
                        await onSave();
                        handleNextClick();
                      } else {
                        handleNextClick();
                      }
                    }}
                  >

                    {isFourthStep() ? "Sljedeći korak" : nextButtonText}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )
    });
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={6}></Grid>
        <Grid item xs={12} sm={6}>
          <Box textAlign="right">
            {deleteBtn ? (
              <ButtonConfirmDialog
                color="inherit"
                btnText={deleteBtnText || "Obriši"}
                btnIcon={<DeleteIcon />}
                title={deleteModalTitle || "Brisanje"}
                message={
                  deleteModalMessage ||
                  "Potvrdom ove akcije brišete zahtjev. Jeste li sigurni da želite obrisati zatjev?"
                }
                cancelBtnText={deleteModalCancelBtnText || "Odustani"}
                okBtnText={deleteModalOkBtnText || "Obriši"}
                onOkClick={handleDelete}
                btnClassName={classes.deleteBtn}
              />
            ) : (
              <></>
            )}
            {saveBtn ? (
              <ButtonConfirmDialog
                color="default"
                btnText={saveBtnText || "Privremena pohrana"}
                btnIcon={<SaveIcon />}
                title={saveModalTitle || "Privremena pohrana"}
                message={
                  saveModalMessage ||
                  "Potvrdom ove akcije šaljete zahtjev na pohranu. Jeste li sigurni da želite pohraniti zatjev?"
                }
                cancelBtnText={saveModalCancelBtnText || "Odustani"}
                okBtnText={saveModalOkBtnText || "Pohrani"}
                onOkClick={handleSave}
                btnClassName={classes.saveBtn}
              />
            ) : (
              <></>
            )}
          </Box>
        </Grid>
      </Grid>

      <Stepper
        activeStep={activeStep}
        className={classes.stepper}
        alternativeLabel
      >
        {steps.map(step => (
          <Step key={step}>
            <StepLabel>{step}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {getStepContent(activeStep)}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  stepper: {
    backgroundColor: "transparent"
  },
  saveBtn: {
    background: "white",
    color: "black",
    marginRight: "20px"
  },
  deleteBtn: {
    background: theme.palette.error.main,
    color: "white",
    marginRight: "20px",
    "&:hover": {
      background: theme.palette.error.dark
    }
  }
}));
